import React, { useEffect, useRef, useState } from "react";
import SeccionCard from "../SeccionCard";
import { guardarHallazgo } from "../guardado";

/* ============================================================================
   Revisión por sistemas / Examen físico  (Sección 6 del rediseño)

   - Un selector de DOS opciones excluyentes por fila: Normal / Hallazgo.
     Ninguna = "sin evaluar" (punto ámbar + borde punteado).
   - Los valores guardados son los MISMOS de siempre:
       Normal   -> sp = true,  cp = false
       Hallazgo -> cp = true,  sp = false
       sin eval -> cp = false, sp = false
   - El campo de observación aparece SOLO en "Hallazgo"; si se vuelve a "Normal"
     se oculta pero no se borra hasta guardar.
   - NO hay «Marcar todo normal» (descartado por criterio clínico).
   - Autoguardado por fila (al cambiar el selector; la observación al salir).
   - "Ver la del <fecha>" abre la consulta anterior en panel de solo lectura.
     NO copia nada.
   ========================================================================== */

const Radio = ({ checked, onClick, className, children, disabled }) => (
  <button
    type="button"
    role="radio"
    aria-checked={checked}
    className={className}
    disabled={disabled}
    onClick={onClick}
  >
    {children}
  </button>
);

function FilaSistema({ fila, onCambiar, bloqueado }) {
  const sinEvaluar = fila.cp !== true && fila.sp !== true;
  const esHallazgo = fila.cp === true;
  const [obs, setObs] = useState(fila.observacion || "");
  const obsRef = useRef(fila.observacion || "");

  useEffect(() => {
    setObs(fila.observacion || "");
    obsRef.current = fila.observacion || "";
  }, [fila.observacion]);

  const setEstado = (destino) => {
    if (bloqueado) return;
    // toggle: volver a pulsar el activo -> "sin evaluar"
    let next = { cp: false, sp: false };
    if (destino === "normal" && !fila.sp) next = { cp: false, sp: true };
    if (destino === "hallazgo" && !fila.cp) next = { cp: true, sp: false };
    onCambiar({ ...fila, ...next, observacion: obsRef.current }, true);
  };

  return (
    <div className={"cev__sy " + (sinEvaluar ? "cev__sy--pend" : "")} role="radiogroup" aria-label={fila.descripcion}>
      {sinEvaluar && <span className="dot-pend" aria-hidden="true" title="Sin evaluar" />}
      <span className="nm">{fila.descripcion}</span>
      <span className="cev__sw">
        <Radio
          className="n"
          checked={fila.sp === true}
          disabled={bloqueado}
          onClick={() => setEstado("normal")}
        >
          Normal
        </Radio>
        <Radio
          className="h"
          checked={fila.cp === true}
          disabled={bloqueado}
          onClick={() => setEstado("hallazgo")}
        >
          Hallazgo
        </Radio>
      </span>
      {esHallazgo && (
        <input
          type="text"
          className="form-control cev__sy-obs"
          placeholder="¿Qué se encontró?"
          value={obs}
          disabled={bloqueado}
          onChange={(e) => {
            setObs(e.target.value);
            obsRef.current = e.target.value;
          }}
          onBlur={() => onCambiar({ ...fila, observacion: obsRef.current }, true)}
        />
      )}
    </div>
  );
}

export default function SeccionHallazgos({
  seccionId, // "sistemas" | "fisico"
  filas, // [{id_antecedente, descripcion, cp, sp, observacion}]
  onCambiarFilas,
  ctx,
  bloqueado,
  onNavegar,
  registrarEstado,
  onVerAnterior,
  fechaAnterior,
  amberFoot,
  amberMsg,
}) {
  const [estado, setEstado] = useState("idle");
  const [savedAt, setSavedAt] = useState(null);
  const runIdRef = useRef(0);

  const evaluados = (filas || []).filter((f) => f.cp === true || f.sp === true).length;
  const total = (filas || []).length;
  const completo = total > 0 && evaluados === total;

  useEffect(() => {
    registrarEstado(seccionId, {
      status: estado,
      savedAt,
      isDirty: estado === "saving" || estado === "error",
      flush: async () => {},
      retry: () => guardarTodasPendientes(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado, savedAt]);

  const persistirFila = async (nuevaFila) => {
    const runId = ++runIdRef.current;
    setEstado("saving");
    const res = await guardarHallazgo(ctx, nuevaFila);
    if (runId !== runIdRef.current) return;
    if (res.success) {
      setSavedAt(new Date());
      setEstado("saved");
    } else {
      setEstado("error");
    }
  };

  const guardarTodasPendientes = async () => {
    for (const f of filas || []) {
      if (f._pendiente) {
        // eslint-disable-next-line no-await-in-loop
        await persistirFila(f);
      }
    }
  };

  const handleCambiar = (nuevaFila, guardar) => {
    const next = (filas || []).map((f) => (f.id_antecedente === nuevaFila.id_antecedente ? nuevaFila : f));
    onCambiarFilas(next);
    if (guardar) persistirFila(nuevaFila);
  };

  return (
    <SeccionCard
      seccionId={seccionId}
      autosave={{ status: estado, savedAt, retry: guardarTodasPendientes }}
      headerExtra={
        <span className={"cev__tag " + (completo ? "cev__tag--green" : "cev__tag--amber")}>
          {total > 0 ? `${evaluados} de ${total} evaluados` : "sin catálogo"}
        </span>
      }
      onNavegar={onNavegar}
      amberFoot={amberFoot}
      amberMsg={amberMsg}
    >
      {fechaAnterior && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
          <button type="button" className="cev__btn cev__btn--ghost cev__btn--sm" onClick={onVerAnterior}>
            Ver la del {fechaAnterior}
          </button>
        </div>
      )}

      {total === 0 ? (
        <div className="cev__empty">No hay catálogo de {seccionId === "fisico" ? "regiones" : "sistemas"} configurado.</div>
      ) : (
        <div className="cev__sg">
          {filas.map((f) => (
            <FilaSistema key={f.id_antecedente} fila={f} onCambiar={handleCambiar} bloqueado={bloqueado} />
          ))}
        </div>
      )}

      <div style={{ marginTop: 11, paddingTop: 10, borderTop: "1px solid var(--border-soft)", fontSize: 12, color: "var(--text-3)" }}>
        Un clic por {seccionId === "fisico" ? "región" : "sistema"}. Ninguna opción marcada = sin evaluar.
        La observación aparece solo cuando marcas «Hallazgo».
      </div>
    </SeccionCard>
  );
}
